import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import LeftMenu from "../components/LeftMenu";
import BookingPagePersonalizationCard from "../components/trial-requests/BookingPagePersonalizationCard";
import BookingPageSettingsCard from "../components/trial-requests/BookingPageSettingsCard";
import EmbedWidgetCard from "../components/trial-requests/EmbedWidgetCard";
import MemberPortalShareCard from "../components/trial-requests/MemberPortalShareCard";
import PortalFeaturesCard from "../components/website/PortalFeaturesCard";
import WebsiteAiAssistant from "../components/website/WebsiteAiAssistant";
import WebsiteContentCard from "../components/website/WebsiteContentCard";
import WebsiteImagesCard from "../components/website/WebsiteImagesCard";
import WebsiteLinksCard from "../components/website/WebsiteLinksCard";
import WebsiteLinksPageCard from "../components/website/WebsiteLinksPageCard";
import WebsiteServicesCard from "../components/website/WebsiteServicesCard";
import { selectIsOrgOwnerOrAdmin } from "../features/authentication/authenticationSlice";
import { selectBookingPageSettings } from "../features/trial-requests/trial-requests-slice";
import { fetchBookingPageSettings } from "../features/trial-requests/slice-extensions/trial-requests-slice-extension";

/**
 * The instructor's public web presence: enable/share the page (link + QR),
 * choose its style (compact booking page vs full landing page), edit the
 * landing content, and grab the embed widgets for an existing website.
 */
const WebsitePage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const settings = useAppSelector(selectBookingPageSettings);
  const isOrgOwnerOrAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);

  useEffect(() => {
    dispatch(fetchBookingPageSettings());
  }, [dispatch]);

  const landing = settings?.enabled && settings.layout === "Landing";

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full min-w-0 p-4 pb-10 lg:px-10">
          <h1 className="mb-1 text-2xl font-bold text-grey-20 dark:text-white">
            {t("website.title", "Website")}
          </h1>
          <p className="mb-4 text-sm text-grey-40 dark:text-grey-80">
            {t("website.pageIntro", "Your public page on timeplanned.com — share it, style it, or embed it into your own site.")}
          </p>

          <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
            <BookingPageSettingsCard />
            <BookingPagePersonalizationCard />
          </div>

          <WebsiteAiAssistant />

          {settings?.enabled && (
            <div className="mb-4">
              <WebsiteContentCard />
            </div>
          )}

          {landing && (
            <div className="mb-4 grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
              <WebsiteImagesCard />
              <WebsiteServicesCard />
            </div>
          )}

          {settings?.enabled && (
            <div className="mb-4 grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
              <WebsiteLinksCard />
              <WebsiteLinksPageCard />
            </div>
          )}

          {settings?.enabled && (
            <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
              <MemberPortalShareCard />
              <PortalFeaturesCard readOnly={!isOrgOwnerOrAdmin} />
            </div>
          )}
          {!settings?.enabled && (
            <div className="mb-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
              <PortalFeaturesCard readOnly={!isOrgOwnerOrAdmin} />
            </div>
          )}

          {/* Full width — the snippets and styling controls need the room. */}
          <EmbedWidgetCard />
        </div>
      </div>
    </section>
  );
};

export default WebsitePage;
